import React, { useState } from "react";
import { Box, Card, CardContent, Skeleton, Theme, Tooltip, Typography, useMediaQuery } from "@mui/material";
import {
    EXAM_BOOKMARK_LOGO_URL,
} from "../../../../utils/constants";
import { ExamCodeResponse } from "../../../../api/interfaces/Code";
import { SkeletonDashboardExamCodes } from "../../../../components/skeleton/SkeletonDashboardExamCodes/SkeletonDashboardExamCodes";
import PinnacleDrawer from "../../../../components/PinnacleDrawer/PinnacleDrawer";
import TakeExamDetails from "./drawer/TakeExamDetails";

interface ExamCodeProps {
    activatedExamCodes: ExamCodeResponse[];
    isLoading: boolean;
}

export const ExamCode: React.FC<ExamCodeProps> = ({ activatedExamCodes, isLoading }) => {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

    const hasExams = activatedExamCodes && activatedExamCodes.length > 0;
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    
    // Exam clicked details state
    const [selectedExamCodeId, setSelectedExamCodeId] = useState<string>('');
    const [selectedExamCodeName, setSelectedExamCodeName] = useState<string>('');
    const [selectedExamCode, setSelectedExamCode] = useState<string>('');
    const [selectedExamCodeDesc, setSelectedExamCodeDesc] = useState<string>('');

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedExamCodeId('');
        setSelectedExamCodeName('');
        setSelectedExamCode('');
        setSelectedExamCodeDesc('');
    }

    const handleExamCodeDetailsDrawerOpen = (examCodeId: string, examCodeName: string, examCode: string, examCodeDesc: string) => {
        setSelectedExamCodeId(examCodeId);
        setSelectedExamCodeName(examCodeName);
        setSelectedExamCode(examCode);
        setSelectedExamCodeDesc(examCodeDesc);
        setIsDrawerOpen(true);
    }

    return (
        <>
            <Card sx={{ mb: 3, borderRadius: '8px', '--Paper-shadow': 'none !important' }} id="take-exam-section">
                <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                    {/* Header with title and logo */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                        }}
                    >
                        <Typography variant="h3" fontWeight={600}>
                            Available Exams
                        </Typography>

                        <Box
                            component="img"
                            src={EXAM_BOOKMARK_LOGO_URL}
                            alt="Exam Bookmark Logo"
                            sx={{
                                width: 40,
                                height: 40,
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    {isLoading ? (
                        <SkeletonDashboardExamCodes />
                    ) : hasExams ? (
                        // Scrollable list of exams
                        <Box
                            sx={{
                                flexGrow: 1,
                                overflowY: "auto",
                                maxHeight: 5 * 72,
                                pr: 1.5,
                                "&::-webkit-scrollbar": {
                                    width: "6px",
                                    height: "6px"
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#d1c4e9",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            {activatedExamCodes.map((item, index) => (
                                <Box
                                    key={item._id}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "#F7F8FD" : "#FFFFFF",
                                        width: "100%",
                                        borderRadius: "8px",
                                        p: '14px 16px',
                                        cursor: "pointer"
                                    }}
                                    onClick={() => handleExamCodeDetailsDrawerOpen(item._id, item.name, item.code, item.description)}
                                >
                                    {/* Main Row Layout */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                            gap: '13px'
                                        }}
                                    >
                                        {/* LEFT SIDE */}
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            {/* Exam Name */}
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "#5840BA", fontWeight: 500 }}
                                            >
                                                {item.name}
                                            </Typography>

                                            {/* Total + Available Exams — IN ONE ROW */}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                                    <Typography variant="caption" sx={{ color: "#818181" }}>
                                                        Total Exams: {item.totalExams}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                                    <Typography variant="caption" sx={{ color: "#818181" }}>
                                                        Available Exams: {item.availableExams}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* RIGHT SIDE — Exam Code (Vertically Centered) */}
                                        {!isMobile && (
                                            <Tooltip title={item.code} arrow>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "#6246EA",
                                                        color: "#FFFFFF",
                                                        borderRadius: "6px",
                                                        p: "8px 12px",
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        minWidth: { xs: "auto", md: 170 },
                                                        height: "36px",
                                                        maxWidth: "100%",
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            whiteSpace: "nowrap",
                                                            flexShrink: 0,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        EXAM CODE:
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: "11px",
                                                            fontStyle: "italic",
                                                            fontWeight: 500,
                                                            opacity: 0.9,
                                                            letterSpacing: 0,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            flexShrink: 1,
                                                            maxWidth: "60%",
                                                            textAlign: "right",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {item.code}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        // Empty State
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 6,
                                textAlign: "center",
                            }}
                        >
                            {/* <Box
                                component="img"
                                src={EMPTY_STATE_ILLUSTRATION_URL}
                                alt="No Exams Available"
                                sx={{
                                    width: 160,
                                    height: 160,
                                    mb: 2,
                                    opacity: 0.85,
                                }}
                            /> */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#818181",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    mt: 0.5,
                                }}
                            >
                                No data to display
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
            <PinnacleDrawer
                title={selectedExamCodeName}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
                customWidthSize={600}
            >
                <TakeExamDetails
                    examCodeId={selectedExamCodeId}
                    examCode={selectedExamCode}
                    examCodeName={selectedExamCodeName}
                    examCodeDesc={selectedExamCodeDesc}
                />
            </PinnacleDrawer>
        </>
    );
};
